/**
 * Module: PhoneInput
 * Role: Atom with fixed +7 prefix and RU phone mask (+7 (903) 754-87-99).
 * Uses: react, styles/tokens, lib/phone
 * Used by: RequisitesScreen (PhoneStep)
 * Glossary: ai/glossary/buyer.md
 *
 * Atom purity: imports only react + tokens + lib/phone.
 * No API calls, no platform access, no hooks beyond useState/useRef/useLayoutEffect.
 */
import { CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import { P, S, T } from '../../styles/tokens';
import { extractNationalDigits, formatRuPhone, toCanonicalPhone } from '../../lib/phone';

export interface PhoneInputProps {
  value: string;
  onChange: (canonical: string) => void;
  error?: string;
  autoFocus?: boolean;
}

/** Minimum caret position — after '+7 (' prefix (4 chars). */
const MIN_CARET = 4;

function buildInputStyle(focused: boolean, hasError: boolean): CSSProperties {
  const borderColor = hasError
    ? P.danger
    : focused
      ? P.terracotta
      : S.separatorOpaque;
  const shadow = hasError
    ? `0 0 0 3px rgba(209,67,67,0.18)`
    : focused
      ? `0 0 0 3px ${S.focusRing}`
      : 'none';

  return {
    width: '100%',
    height: 48,
    borderRadius: 12,
    border: `1.5px solid ${borderColor}`,
    background: P.card,
    color: P.ink,
    fontFamily: T.fontBody,
    fontSize: 16,
    fontWeight: 400,
    padding: '0 14px',
    boxSizing: 'border-box',
    outline: 'none',
    boxShadow: shadow,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };
}

const errorTextStyle: CSSProperties = {
  marginTop: 4,
  font: T.footnote,
  color: P.danger,
};

export function PhoneInput({ value, onChange, error, autoFocus }: PhoneInputProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  // Derive national digits from canonical value prop
  const national = extractNationalDigits(value);
  const displayValue = formatRuPhone(national);

  // Restore caret position after re-render (format changes shift chars)
  useLayoutEffect(() => {
    if (caretRef.current !== null && inputRef.current) {
      const pos = Math.max(MIN_CARET, caretRef.current);
      inputRef.current.setSelectionRange(pos, pos);
      caretRef.current = null;
    }
  });

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const rawValue = e.target.value;
    const selectionBefore = e.target.selectionStart ?? rawValue.length;

    // Count digits before caret in the raw input value
    const digitsBefore = rawValue.slice(0, selectionBefore).replace(/\D/g, '').length;

    const newNational = extractNationalDigits(rawValue);
    const newFormatted = formatRuPhone(newNational);

    // Find position in new formatted string where digitsBefore digits have passed
    let digitsSeen = 0;
    let newCaret = newFormatted.length;
    for (let i = 0; i < newFormatted.length; i++) {
      if (/\d/.test(newFormatted[i])) {
        digitsSeen++;
        if (digitsSeen === digitsBefore) {
          newCaret = i + 1;
          break;
        }
      }
    }

    caretRef.current = Math.max(MIN_CARET, newCaret);
    onChange(toCanonicalPhone(newNational));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Prevent caret from moving into the fixed '+7 (' prefix
    const input = e.currentTarget;
    if (
      (e.key === 'Backspace' || e.key === 'ArrowLeft') &&
      (input.selectionStart ?? 0) <= MIN_CARET &&
      (input.selectionEnd ?? 0) <= MIN_CARET
    ) {
      e.preventDefault();
    }
  }

  function handleFocus() {
    setFocused(true);
    // Place caret at end on focus
    setTimeout(() => {
      if (inputRef.current) {
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }, 0);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <input
        ref={inputRef}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={() => setFocused(false)}
        type="tel"
        inputMode="tel"
        autoFocus={autoFocus}
        style={buildInputStyle(focused, !!error)}
        aria-invalid={error ? true : undefined}
        aria-label="Телефон СБП"
      />
      {error && <span style={errorTextStyle}>{error}</span>}
    </div>
  );
}
